import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { ANNUAL_MASK_STATUS, ANNUAL_MASK_SOCIAL_SECURITY_DECISION, canAdministerAnnualMaskPurchase, getAnnualMaskSocialSecurityDecision } from "./annualMaskPurchaseUtils";
import { getAnnualMaskPhysicianConstraints, isAnnualMaskPhysician } from "./annualMaskPhysicianQuery";

export const canUploadAnnualMaskCertificate = (caseItem, user) => {
  if (!caseItem || !user) return false;
  if (isAnnualMaskPhysician(user)) {
    return getAnnualMaskPhysicianConstraints(user).some(([field, value]) => caseItem[field] === value);
  }
  return canAdministerAnnualMaskPurchase(user);
};

export const isAnnualMaskCertificateUploadLocked = (caseItem = {}) =>
  Boolean(caseItem.saleOrderId) ||
  [ANNUAL_MASK_STATUS.READY_TO_PURCHASE, ANNUAL_MASK_STATUS.ORDER_CREATED, ANNUAL_MASK_STATUS.ORDER_COMPLETED].includes(caseItem.status) ||
  getAnnualMaskSocialSecurityDecision(caseItem) === ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PASSED;

export const getAnnualMaskPhysicianCertificateState = (caseItem = {}) =>
  caseItem.signedMedicalCertificate ? "Uploaded" : isAnnualMaskCertificateUploadLocked(caseItem) ? "Locked" : "Awaiting upload";

export const saveAnnualMaskSignedCertificate = async ({ database, caseItem, certificate, currentUser }) =>
  runTransaction(database, async (transaction) => {
    const caseRef = doc(database, caseItem.refPath);
    const snapshot = await transaction.get(caseRef);
    if (!snapshot.exists()) throw new Error("This Annual Mask Purchase case no longer exists.");
    const current = snapshot.data();
    if (!canUploadAnnualMaskCertificate(current, currentUser)) throw new Error("You can upload a certificate only for a case assigned to you.");
    if (isAnnualMaskCertificateUploadLocked(current)) throw new Error("The case has advanced. Ask an administrator before replacing its certificate.");
    if (current.hn !== caseItem.hn) throw new Error("The patient details changed. Refresh this case before uploading.");
    const sourceRef = current.sourceFollowupPath ? doc(database, current.sourceFollowupPath) : null;
    const source = sourceRef ? await transaction.get(sourceRef) : null;
    const link = source && source.exists() && (source.data().extendedProps || {}).annualMaskPurchase;
    const isCurrentLink = link && (link.casePath === caseRef.path ||
      (caseItem.batchId && caseItem.id && link.batchId === caseItem.batchId && link.caseId === caseItem.id));
    const actor = currentUser.displayName || currentUser.email || "";
    transaction.update(caseRef, {
      signedMedicalCertificate: certificate,
      signedCertificateUploadSkip: null,
      signedMedicalCertificateUploadedAt: serverTimestamp(),
      signedMedicalCertificateUploadedBy: actor,
      socialSecurityDecision: ANNUAL_MASK_SOCIAL_SECURITY_DECISION.PENDING,
      socialSecurityDecisionAt: null,
      socialSecurityDecisionBy: "",
      socialSecurityApproved: false,
      socialSecurityApprovedAt: null,
      socialSecurityApprovedBy: "",
      status: ANNUAL_MASK_STATUS.SIGNED_CERTIFICATE_UPLOADED,
      updatedAt: serverTimestamp(), updatedBy: actor,
    });
    if (isCurrentLink) transaction.update(sourceRef, {
      "extendedProps.annualMaskPurchase.status": ANNUAL_MASK_STATUS.SIGNED_CERTIFICATE_UPLOADED,
      "extendedProps.annualMaskPurchase.updatedAt": serverTimestamp(),
    });
    return current.signedMedicalCertificate;
  });
